import { useMemo } from 'react';
import { create } from 'zustand';
import type {
  MeResponse,
  SessionResponse,
  AuthTokenResponse,
  MemberAvatarResponse,
} from '@kidshome/api-contract';
import { createCan } from '@/shared/lib/permissions';

/**
 * Auth store (Zustand) — the in-memory session. The access token is NEVER persisted (memory only,
 * per the spec); only the refresh token lives in the httpOnly cookie the API sets. `user` comes from
 * `GET /v1/auth/me`. `csrfToken` is the readable double-submit token echoed on cookie routes.
 *
 * Token lifecycle is driven by the api-contract mutator (transparent 401 -> refresh -> retry); this
 * store exposes the setters the mutator's auth bridge reads/writes (see `app/api-client.ts`).
 */
/**
 * Scoped impersonation (ADR-0055): the household a platform owner is currently "acting in", held in
 * memory only. While set, the api-client echoes `X-Act-As-Household: <householdId>` on every request
 * and the persistent banner names the household. `name` is RAW user content (never translated).
 */
export interface ActingAs {
  readonly householdId: string;
  readonly name: string;
}

export interface AuthState {
  accessToken: string | null;
  user: MeResponse | null;
  csrfToken: string | null;
  status: 'idle' | 'authenticated' | 'unauthenticated';
  /** Non-null only while a platform owner is acting inside another household (ADR-0055). */
  actingAs: ActingAs | null;

  setSession: (session: SessionResponse) => void;
  setTokens: (tokens: AuthTokenResponse) => void;
  setUser: (user: MeResponse | null) => void;
  /** Mirror a self-service locale change into the in-memory profile (no-op when signed out). */
  setUserLocale: (locale: string | null) => void;
  /** Mirror an avatar change into the in-memory profile so the shell updates (no-op when signed out). */
  setUserAvatar: (avatar: MemberAvatarResponse) => void;
  setCsrfToken: (token: string | null) => void;
  /** Enter scoped impersonation of a household (ADR-0055) — set after the `enter` endpoint succeeds. */
  setActingAs: (actingAs: ActingAs | null) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  csrfToken: null,
  status: 'idle',
  actingAs: null,

  setSession: (session) =>
    set({
      accessToken: session.auth.accessToken,
      user: session.me,
      status: 'authenticated',
    }),
  setTokens: (tokens) => set({ accessToken: tokens.accessToken }),
  setUser: (user) => set({ user, status: user ? 'authenticated' : 'unauthenticated' }),
  setUserLocale: (locale) => set((s) => (s.user ? { user: { ...s.user, locale } } : {})),
  setUserAvatar: (avatar) => set((s) => (s.user ? { user: { ...s.user, avatar } } : {})),
  setCsrfToken: (token) => set({ csrfToken: token }),
  setActingAs: (actingAs) => set({ actingAs }),
  clear: () =>
    set({
      accessToken: null,
      user: null,
      csrfToken: null,
      status: 'unauthenticated',
      actingAs: null,
    }),
}));

/** Selector: a `can()`/`canAny()` checker bound to the current user's permissions (memoized). */
export function useCan(): ReturnType<typeof createCan> {
  const permissions = useAuthStore((s) => s.user?.permissions);
  return useMemo(() => createCan(permissions ?? []), [permissions]);
}

/**
 * Selector: a `hasFeature(code)` checker bound to the household's enabled optional features (ADR-0073).
 * Hides UI for features the household has disabled (the API also enforces). Core modules (identity/
 * auth/health/compliance) are always on and are never in this set — `hasFeature` is for the optional
 * catalog only, so callers gate ONLY optional-module surfaces with it.
 */
export function useFeature(): (code: string) => boolean {
  const features = useAuthStore((s) => s.user?.householdFeatures);
  return useMemo(() => {
    // No feature set resolved yet (signed out / pre-bootstrap) → don't hide anything; the shells that
    // call this only render once `me` is loaded, and the API enforces regardless. Once the household's
    // set IS known (an array, even empty), gate strictly on it so a disabled module disappears.
    if (features == null) {
      return () => true;
    }
    const set = new Set(features);
    return (code: string) => set.has(code);
  }, [features]);
}
